<template>
  <div>
    <!-- 성능탭 내 버튼그룹 -->
    <div>
      <div class="tabs-layout-wrap">
        <lego-segment-wrapper v-model="activeTab">
          <lego-segment-button value="detail" @click="switchTab('detail')">
            <sc-icon
              name="ri-list-view-new"
              :style="isThumbnail ? 'color: #868D9C' : 'color: #2C59D9'"
              :size="16"
              v-lego-tooltip="$t('button.detail')"
            />
          </lego-segment-button>
          <lego-segment-button value="summary" @click="switchTab('summary')">
            <sc-icon
              name="ri-grid_view_new"
              :style="!isThumbnail ? 'color: #868D9C' : 'color: #2C59D9'"
              :size="16"
              v-lego-tooltip="$t('button.summary')"
            />
          </lego-segment-button>
          <div v-if="isCdn" class="chart-guide">
            <lego-icon
              iconType="line"
              icon="information"
              :size="20"
              style="vertical-align: middle; marign-top: -1px"
            />
            {{ t('label.cdnGuide') }}
          </div>
        </lego-segment-wrapper>
        <lego-button border primary @click="openPerformanceComparePopup"
          >{{ t('button.performanceCompare') }}
        </lego-button>
      </div>
    </div>
    <!-- 성능탭 내 차트 레이아웃 -->
    <div class="mon-target-performance">
      <lego-dashboard v-show="isThumbnail" :col="4" :row="1" :gap="12" :vsize="194">
        <lego-dashboard-item
          v-for="(item, idx) in metricList"
          :x="parseInt(idx % 4)"
          :y="parseInt(idx / 4)"
          :key="item.metricKey"
          :widthContent="1234"
        >
          <SummaryMetricChart
            :fetchStatus="fetchStatus.summary"
            :refresh="refresh"
            :metric="item"
            :date-option="searchDate"
            :search-model="searchModel"
            :selectedTargets="[params]"
            :collInterval="interval"
            :isNow="searchDate.isNow"
            parentClass="lego-dashboard"
            @onFetched="markFetch"
          >
          </SummaryMetricChart>
        </lego-dashboard-item>
      </lego-dashboard>
      <c-scroll
        :classStyle="'cus-c-scroll-height-width chart-detail-scroll'"
        :style="{ height: scrollHeight }"
        style="padding-right: 14px"
      >
        <div
          v-for="(item, idx) in metricList"
          :key="item.metricKey"
          :style="{
            visibility: isThumbnail ? 'hidden' : 'visible',
            height: isThumbnail ? 0 : 'max-content',
            overflow: isThumbnail ? 'hidden' : 'initial'
          }"
        >
          <DetailMetricChart
            :fetchStatus="fetchStatus.detail"
            :metric="item"
            :date-option="searchDate"
            :search-model="searchModel"
            :selectedTargets="[params]"
            :collInterval="interval"
            :isNow="searchDate.isNow"
            @on-zoom="$emit('on-zoom')"
            @add-widget="(chartType) => addWidgetPopup(item, idx, chartType)"
            @add-event-policy="eventAddModal(item)"
            @onFetched="markFetch"
          >
          </DetailMetricChart>
        </div>
      </c-scroll>
    </div>
    <PerformanceComparePopup
      v-if="isPerformanceComparePopup"
      :selTarget="[modalParam]"
      :prodTypeCode="modalParam.productTypeCode"
      @open-event-add-setting="openEventAddSetting"
      @close="popupClose"
    />
    <AddWidgetPopup
      v-if="isShowAddWidget"
      :searchDate="searchDate"
      mode="externalAdd"
      :config="widgetConfig"
      @close="isShowAddWidget = !isShowAddWidget"
      @widgetAdd="onWidgetAdd"
    />
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import i18nMixin from '@/mixins/i18n.mixin.js';
import monTargetMixin from './monTarget.mixin.js';
import PerformanceComparePopup from '@/containers/performance/PerformanceComparePopup.vue';
import { numberOfDownloadFunc } from '../performance/performanceUtil';
import {
  downloadMetricDataExcel as downloadExcel,
  downloadMetricDataImage as downloadImage,
  downloadRawMetricDataExcel as downloadRawExcel
} from '@/containers/performance/productMetricLib.js';
import AddWidgetPopup from '../widget/AddWidgetPopup.vue';
import widgetAddConfigMixin from '../widget/configs/widgetAddConfigMixin';
import legoEventBus from '@/lego-component/utils/eventBus/eventBus';
import { useDateTimeFormat } from '@/composables/useDateTimeFormat';
import { useChart } from '@/composables/useChart.js';
import DetailMetricChart from '../components/metric/DetailMetricChart.vue';
import SummaryMetricChart from '../components/metric/SummaryMetricChart.vue';

const { formatDateTime, FULL_TIME_FORMAT } = useDateTimeFormat();

const TAB = {
  DETAIL: 'detail',
  SUMMARY: 'summary'
};
export default {
  name: 'MonTargetPerformance',
  emits: ['cdn-order', 'close', 'open-event-add-setting', 'toggle-sub-modal', 'on-zoom'],
  props: {
    params: {
      type: Object,
      default: () => {}
    },
    scrollHeight: {
      type: String,
      default: ''
    },
    searchDate: {
      type: Object,
      default: () => {}
    }
  },
  mixins: [i18nMixin, monTargetMixin, widgetAddConfigMixin],
  inject: ['sharedInterval'],
  components: {
    SummaryMetricChart,
    DetailMetricChart,
    AddWidgetPopup,
    PerformanceComparePopup
  },
  computed: {
    // 검색 기간 날짜 차이(day)
    searchDatePeriod() {
      return this.searchDate
        ? moment(this.searchDate.queryEndDt).diff(moment(this.searchDate.queryStartDt), 'days')
        : 0;
    },
    isCdn() {
      return this.productTypeCode && this.productTypeCode.toUpperCase() === 'CDN';
    },
    interval() {
      return this.sharedInterval.interval;
    },
    isThumbnail() {
      return this.activeTab === TAB.SUMMARY;
    }
  },

  watch: {
    searchDate: {
      deep: true,
      handler() {
        this.resetTab();
      }
    }
  },

  data() {
    return {
      refresh: 0,
      isShowAddWidget: false,
      activeTab: TAB.SUMMARY, // 차트 요약보기/상세보기 toggle
      isPerformanceComparePopup: false, // 성능비교 modal
      modalParam: {}, // 모달 param
      productTypeCode: '', // 상품 유형
      objectType: '',
      metricList: [], // 주요 metrics
      dashboardItems: [], // 차트 정보
      apiParams: {
        // metric-data api params
        metricDataConditionConsole: {
          metricKey: '',
          objectType: '',
          productResourceInfosConsole: [
            {
              productResourceId: this.params.productResourceId
            }
          ],
          statisticsPeriod: null,
          statisticsTypeList: ['MAX']
        },
        queryStartDt: this.getSearchDate().queryStartDt,
        queryEndDt: this.getSearchDate().queryEndDt
      },
      // 대시보드 레이아웃 옵션
      layoutOption: {
        col: 4,
        row: 1,
        gap: 12,
        vsize: 194
      },
      vpcIsObjectExist: [],
      cdnMetricOrders: [],
      isProductAuth: true,
      statisticsType: {
        time: [],
        timeOrg: [
          //차트 표시 시간단위(5분,10분,30분,1시간,3시간,6시간)
          {
            value: '5',
            text: '5' + this.$t('label.minute'),
            '3d': false,
            '7d': false,
            '14d': false
          },
          {
            value: '10',
            text: '10' + this.$t('label.minute'),
            '3d': true,
            '7d': false,
            '14d': false
          },
          {
            value: '30',
            text: '30' + this.$t('label.minute'),
            '3d': true,
            '7d': true,
            '14d': false
          },
          {
            value: '60',
            text: '1' + this.$t('label.hour'),
            '3d': true,
            '7d': true,
            '14d': true
          },
          {
            value: '180',
            text: '3' + this.$t('label.hour'),
            '3d': true,
            '7d': true,
            '14d': true
          },
          {
            value: '360',
            text: '6' + this.$t('label.hour'),
            '3d': true,
            '7d': true,
            '14d': true
          }
        ]
      },

      dateOption: {
        startDate: '',
        endDate: '',
        formatter: '{HH}:{mm}'
      },
      searchModel: {
        productTypeCode: '',
        subTypeCode: '',
        productTypeItem: {},
        subTypeItem: {}
      },
      fetchStatus: {
        detail: false,
        summary: false
      },
      fetchedTabs: {
        detail: false,
        summary: false
      }
    };
  },
  async created() {
    this.modalParamTitle = this.$t('title.monTargetDetail');
    this.modalParam = this.params;
    this.productResourceId = this.params.productResourceId;
    this.productTypeCode = this.params.productTypeCode;
    this.objectType = this.params.objectTypeCode;
    this.searchModel.productTypeCode = this.params.productTypeCode;
    this.searchModel.subTypeCode = this.params.objectTypeCode;

    const listDataCheckAuth = [
      { productResourceId: this.params.productResourceId, productName: this.params.productName }
    ];
    if (this.modalParam.type === false) {
      this.activeTab = TAB.DETAIL;
      this.fetchStatus.detail = true;
    }
    // 탭 권한 체크
    await this.checkAuthorization(this.productResourceId, this.productTypeCode, listDataCheckAuth);
  },
  mounted() {
    const tabKey = this.isCdn ? TAB.DETAIL : TAB.SUMMARY;
    this.activeTab = tabKey;
    this.fetchStatus[tabKey] = true;
  },
  methods: {
    markFetch(tab) {
      this.fetchedTabs[tab] = true;
    },

    resetTab() {
      this.fetchedTabs[this.activeTab] = false;
      this.fetchStatus.detail = false;
      this.fetchStatus.summary = false;
      setTimeout(() => (this.fetchStatus[this.activeTab] = true), 0);
    },
    addWidgetPopup(item, idx, chartType) {
      let metric = item;
      let objectItems = [];
      const selectedTargets = [
        {
          productSq: this.params.productId,
          productName: this.params.productName,
          resourceId: this.params.productResourceId,
          idx: idx,
          check: this.params.check
        }
      ];
      let products = this.$util.replaceArr(selectedTargets, {
        productName: 'text',
        resourceId: 'key',
        productSq: 'productSq',
        idx: 'idx',
        check: 'check'
      });

      this.widgetConfig = {
        chartAttr: {
          widget: {
            type: 'TimeSeriesGraphWidget', // 위젯 종류 : 시계열 위젯
            options: {
              isDetailPerformance: true,
              chartType: chartType || 'line', // 차트 종류 (line, stack, scatter...)
              isObjectExist: metric.isObjectExist, // 개별항목 존재 여부
              disableObject: metric.disableObject,
              metricName: metric.metricName,
              metricKey: metric.metricKey, // metricKey
              objectItems: objectItems, // 하위 모니터링 대상
              objectType: metric.subTypeCode, // 하위 상품 유형
              objects: [], // 개별항목
              products: products, // 모니터링 대상
              resourceType: this.productTypeCode // 상품유형
            }
          }
        }
      };
      this.isShowAddWidget = true;
    },
    formatNumberWithComma(value) {
      return value.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    },
    checkFinalValue(finalValue, data) {
      return finalValue ? finalValue : data[data.length - 1].value;
    },
    chartYAxisName(metricUnit, unit, maxUnit) {
      return `(${!metricUnit.orgUnit ? unit : maxUnit ? maxUnit : metricUnit.orgUnit})`;
    },
    // 탭 권한 체크
    async checkAuthorization(productResourceId, productTypeCode, listDataCheckAuth = []) {
      const param = {
        productTypeCode: productTypeCode,
        actionName: this.$const.AuthorizationAction.MONITOR_VIEW,
        productResourceIds: [productResourceId],
        listDataCheckAuth
      };

      this.$store.dispatch('authStore/checkProductResource', param).then(async (res) => {
        if (res.type !== this.$const.AuthorizationResult.ALLOW) {
          this.isProductAuth = false;
          return;
        }
        this.isProductAuth = true;
        await this.getMainMetrics();
      });
    },

    // 주요성능 metric 조회
    async getMainMetrics() {
      let params = { productSq: this.modalParam.productId, objectType: this.getObjectType() };

      await this.apiGetMainMetrics(params, this.productTypeCode).then((data) => {
        let vpcIsObjectExist = [];
        let cdnMetricOrders = [];
        this.cdnMetricOrders = [];

        if (this.$util.isNotEmpty(data)) {
          if (this.productTypeCode === 'Kubernetes') {
            data = _.filter(data, (m) => m.metricSetKey == 'kubernetes.cluster');
          }
          this.metricList = data;

          if (this.productTypeCode === 'VPC') {
            vpcIsObjectExist = data.map((v) => v.isObjectExist);
            this.vpcIsObjectExist = vpcIsObjectExist;
          }

          if (this.isCdn) {
            this.metricList.map((p) => cdnMetricOrders.push(p.metricName));
            this.cdnMetricOrders = cdnMetricOrders;
            this.$emit('cdn-order', this.cdnMetricOrders);
          }
        }
      });
    },

    saveFile(selItem, metricItem, mainChart, thumbnail) {
      let _formatter = (value) => this._getDateFormat(value);
      this.dateOption.endDate = this.getSearchDate().queryEndDt;
      this.dateOption.startDate = this.getSearchDate().queryStartDt;
      this.dateOption.formatter = _formatter;
      const { checkDownloadLimited, numberOfDownload, numberOfLimited } = numberOfDownloadFunc(
        this.dateOption,
        metricItem,
        [metricItem]
      );
      if (
        metricItem.metricData.length === 0 ||
        (selItem.type === 'excel-raw' && checkDownloadLimited)
      ) {
        this.$modal.open({
          rootId: 'app',
          type: 'alert',
          title: this.$t('title.notice'),
          // message: this.$t('message.noDownloadData'),
          message: function () {
            if (checkDownloadLimited) {
              return `${this.$t('page.performance.downloadCheckRawData', {
                params: `${numberOfDownload}/${numberOfLimited}`
              })}`;
            } else {
              return `${this.$t('message.noDownloadData')}`;
            }
          }.call(this),
          action: () => {
            return;
          }
        });
      } else {
        this.$modal.open({
          rootId: 'app',
          title: this.$t('title.notice'),
          type: 'confirm',
          message: `<div>` + this.$t('page.performance.downloadCheck') + `</div>`,
          action: () => {
            if (selItem.type === 'image') {
              const dropdownGroup = thumbnail
                ? mainChart.querySelector('.dropdown-group-summary')
                : mainChart.querySelector('.dropdown-group');
              dropdownGroup.style.display = 'none';
              mainChart.style.border = 'none';
              mainChart.style.boxShadow = 'unset';
              let footer;
              if (!thumbnail) {
                footer = mainChart.querySelector('.footer-detail');
                if (footer) footer.style.display = 'none';
              }
              const tooltipIcon = mainChart.querySelector('.tooltip-icon');
              if (tooltipIcon) tooltipIcon.style.display = 'none';

              downloadImage(metricItem.metricData, mainChart);
              if (footer) footer.style.display = 'flex';
              dropdownGroup.style.display = 'flex';
              mainChart.style.border = '1px solid #d2d3d6';
            } else if (selItem.type === 'excel') {
              //console.log("downloadExcel")
              downloadExcel(
                this.dateOption.startDate,
                this.dateOption.endDate,
                metricItem.metricData
              );
            } else {
              this.downloadRawMetricDataExcel(metricItem);
            }
          },
          cancel: () => {
            //console.log('>>> cancel: downloadMetricData');
          }
        });
      }
    },

    downloadRawMetricDataExcel(metric) {
      let params = {
        metricDataConditionConsole: {
          metricKey: metric.key,
          objectType: '',
          productResourceInfosConsole: [],
          statisticsPeriod: metric.statisticsType.time,
          statisticsTypeList: metric.statisticsType.type
        },
        queryStartDt: this.getSearchDate().queryStartDt,
        queryEndDt: this.getSearchDate().queryEndDt,
        rawDataExportYn: 'Y'
      };
      params.metricDataConditionConsole.productResourceInfosConsole.push({
        productResourceId: metric.metricData[0].productResourceId
      });
      this.$http.product.downloadRawMetricData(params, this.productTypeCode).then((data) => {
        downloadRawExcel(this.getSearchDate().queryStartDt, this.getSearchDate().queryEndDt, data);
      });
    },
    eventAddModal(item) {
      this.$emit('open-event-add-setting', this.params, item);
    },

    _getDateFormat(value) {
      const DAY = 24 * 60 * 60 * 1000;
      const datePicker = this.$refs.reloadPicker.getCurrentDate();
      let period = new Date(datePicker.queryEndDt) - new Date(datePicker.queryStartDt);
      let format = moment(value).format('HH:mm');
      if (period > DAY) {
        format = moment(value).format('MM-DD HH').split('');
        format[5] = '\n';
        format = format.join('') + 'h';
      }
      return format;
    },

    // 차트 요약 보기
    switchTab(tab) {
      legoEventBus.$emit('click-outside-datePicker');
      this.activeTab = tab;
      this.fetchStatus[tab] = true;
    },

    // 메뉴 드롭다운
    dropdownMenu() {
      this.isDropdownRefresh = !this.isDropdownRefresh;
    },

    // 성능비교 팝업
    openPerformanceComparePopup() {
      this.isPerformanceComparePopup = true;
      legoEventBus.$emit('click-outside', true);
      this.$emit('toggle-sub-modal', true);
    },

    close() {
      this.$emit('close', true);
    },

    popupClose() {
      this.isPerformanceComparePopup = false;
      this.$emit('toggle-sub-modal', false);
    },
    // 조회시간 설정
    getSearchDate() {
      let queryStartDt,
        queryEndDt,
        chartTimeUnit,
        dateType = 1;

      if (this.searchDate) {
        queryStartDt = this.searchDate.queryStartDt;
        queryEndDt = this.searchDate.queryEndDt;
        chartTimeUnit = this.searchDate.chartTimeUnit;
        dateType = this.searchDate.dateType;
      } else {
        // default date
        queryStartDt = moment(new Date()).add(-6, 'hours').toISOString(); // 6시간 전
        queryEndDt = moment(new Date()).toISOString();
        chartTimeUnit = '5m';
      }

      // Global CDN
      if (dateType === 1 && this.isCdn) {
        queryStartDt = moment(queryStartDt).add(-1, 'days').toISOString();
        queryEndDt = moment(queryEndDt).add(-1, 'days').toISOString();
      }
      return {
        queryStartDt: queryStartDt,
        queryEndDt: queryEndDt,
        chartTimeUnit: chartTimeUnit
      };
    },

    xLabelFormatter(value) {
      let type = this.getSearchDate().chartTimeUnit;
      return this.$util.getDateFormat(value, type);
    },

    // Data Ops or Data Flow 일때 objectType 생성
    getObjectType() {
      if (this.productTypeCode === 'DataOps' || this.productTypeCode === 'DataFlow') {
        return this.objectType === 'manager' ? 'pod' : 'service';
      } else {
        return (this.objectType = '');
      }
    },
    openEventAddSetting(item) {
      this.$emit('open-event-add-setting', item);
    }
  }
};
</script>

<style lang="scss" scoped>
.chart-title {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  &_text {
    max-width: 200px;
  }

  :deep .c-label {
    font-size: 12px !important;
    font-weight: 700 !important;
    color: #2f3237 !important;
  }

  .chart-max-value {
    display: flex;

    span {
      font-size: 13px;
      line-height: 16px;
      font-weight: 500;
      color: #2f3237;
    }

    .metric-unit {
      margin-left: 4px;
    }
  }
}

.chart-guide {
  float: right;
  padding-left: 24px;
  line-height: 32px;
  font-size: 13px;
  color: #585c65;
  display: flex;
  gap: 8px;

  :deep .opus-icon__info:hover,
  .opus-icon__info:active {
    color: rgba(43, 56, 81, 0.5);
  }
}

.cus-c-scroll-height-width {
  width: 100%;
  height: 622px;
  border-radius: 0;
}

.chart-bottom-legend {
  display: flex;
  justify-content: space-between;
  padding-bottom: 16px;
}

.legend-item {
  display: inline-block;
  padding-right: 18px;

  .cus-circle {
    display: inline-block;
    width: 12px;
    height: 12px;
    margin: 5px 4px 3px 0;
    vertical-align: middle;
    background-color: #20bac2;
    border-radius: 50%;
  }
}

.monitoring-info-wrap {
  padding: 24px 20px 20px;
  background-color: var(--pale-grey);

  :deep .c-grid-view-col {
    margin: 0 !important;
  }

  :deep .lego-text-field {
    min-width: initial !important;
  }

  :deep .lego-text-field__input {
    border-color: transparent !important;
    background-color: transparent !important;
  }

  :deep .c-grid-view-col,
  :deep .c-grid-view-col-label,
  :deep .c-grid-view-col-label-text {
    line-height: 32px !important;
  }

  :deep .c-grid-view-col-label {
    width: 150px !important;
  }

  :deep .c-grid-view-col-label-text {
    color: #787c84 !important;
  }

  .gauge-bar {
    margin-top: 3px;
  }
}

.cus-inline-block {
  display: inline-block;
}

.cus-lego-modal__body__noscr :deep .lego-modal__body__noscr {
  padding: 20px 20px 20px 20px;
}

.cus-lego-madal_container :deep .lego-modal__container {
  padding: 0;
  max-height: 100%;
}

.cus-lego-modal__body :deep .lego-modal__body {
  padding: 30px;
  height: 750px;
  max-height: 100%;
}

.tabs-layout-wrap {
  display: flex;
  justify-content: space-between;
  padding: 16px 0;
  text-align: right;
}

.chart-detail-scroll :deep .simplebar-content {
  width: unset !important;
}

.cus-c-scroll-height-width :deep .simplebar-content {
  width: calc(100% - 20px);
}
:deep .lego-dashboard {
  min-height: 500px;
}
:deep .lego-dashboard-item {
  display: flex;
  flex-direction: column;
  &:nth-child(4n + 4),
  &:nth-child(4n + 3) {
    &:not(.detail-chart) {
      .tooltip-wrapper .tooltip-icon .tooltip-container {
        left: auto;
        right: 22px;
      }
    }
  }
}

:deep .lego-segment-wrapper {
  .lego-segment-button.lego-segment-button--medium {
    min-width: auto;
    width: 32px;
    height: 32px;
  }
}

.lego-dropdown-refresh {
  position: absolute;
  top: 33px;
  right: 0;
  width: 129px;
  border: solid 1px var(--blue-with-a-hint-of-purple);
  background-color: #fff;
  z-index: 100;

  & button {
    display: block;
    width: 100%;
    height: 34px;
    padding: 8px 10px;
    text-align: left;
    font-size: 13px;
    color: #1f2533;
    background-color: #fff;
    border: 0;
    outline: none;
    cursor: pointer;

    &.active,
    &:hover {
      color: rgb(54, 79, 199);
      background-color: rgba(174, 184, 233, 0.12);
    }
  }
}

:deep .lego-segment-button {
  path: {
    fill: #1f2533;
    opacity: 0.6;
  }
}

:deep .lego-segment-button--checked {
  path {
    fill: #364fc7;
  }
}

.chart-thumnail-item {
  // height: 230px !important;
  padding: 0;
  border: 1px solid #e3e3e3;
}

.chart-thumnail-title {
  padding: 10px;
}

.chart-detail {
  width: 100%;
  // height: 230px !important;
}

.chart-detail-item {
  padding: 8px 16px 16px 16px;
  border: 1px solid #e3e3e3;

  .chart-detail-content {
    display: flex;
    align-items: flex-end;
    height: 100%;
  }
}

.chart-detail-content {
  height: 100%;
}

.chart-detail-title {
  padding: 10px 10px 0 10px;
}

.chart-no-data.no-data {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  color: rgba(51, 51, 51, 0.4);
  font-weight: 500;
  font-size: 14px;
  width: 100%;
  height: 78px;
  background-color: #f2f5ff;
  gap: 12px;
}

:deep .lego-tooltip {
  max-width: 500px !important;
}

:deep .widget-null {
  width: auto !important;
  min-height: 0 !important;
  margin: 0 auto !important;
  padding: 20px 0 !important;
}
</style>
